import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from './quiz/ConfirmDialog';
import { SoundMuteToggle } from './quiz/QuizAtoms';
import { playSelect } from '../utils/sound';
import logoUrl from '../assets/images/cleveroo-logo.png';
import './WritingTask.css';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface EssayQuestion {
  id: string;
  type: string;
  title?: string;
  subject?: string;
  source_metadata?: {
    naplan?: { genre?: string; promptTitle?: string; yearLevel?: number };
    [key: string]: unknown;
  };
  content?: {
    // Real fields confirmed from production data shape
    promptTitle?: string;
    genre?: string;
    fullPromptText?: string;
    questionText?: string;
    question?: string;
    planningInstructions?: string;
    responseFormat?: string;
    // Legacy / fallback fields kept for safety
    prompt?: string;
    stimulus?: string | { text?: string };
    task?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface TestData {
  id: string;
  title: string;
  description?: string;
  subject?: string;
  time_limit_minutes?: number;
}

interface TestSession {
  sessionId: string;
  test: TestData;
  questions: EssayQuestion[];
  timeLimit: number; // seconds
}

interface PlanState {
  opinion: string;
  r1: string;
  r2: string;
  end: string;
}

interface ChecksState {
  open?: boolean;
  reasons?: boolean;
  connect?: boolean;
  ending?: boolean;
}

interface WTState {
  text: string;
  plan: PlanState;
  checks: ChecksState;
  seconds: number;
  done: boolean;
  tab: 'write' | 'plan';
  mode: 'practice' | 'test';
}

// ─────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────

export interface WritingTaskProps {
  testId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialSession?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onComplete: (result: any) => void;
  onCancel: () => void;
}

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const WORD_GOAL = 200;
const DEFAULT_TIME_LIMIT = 40 * 60;
const RING_LEN = 2 * Math.PI * 11;

const LINKERS = [
  'firstly','secondly','because','however','furthermore','therefore',
  'for example','for instance','most importantly','in addition','finally',
  'in conclusion','moreover','also','since',
];
const OPINION_CUES = ['i think','i believe','in my opinion','i feel','should','must','i strongly'];
const ENDING_CUES = [
  'in conclusion','to sum up','to conclude','overall','in the end',
  'for these reasons','that is why','this is why',
];

const SENTENCE_STARTERS = [
  'In my opinion,',
  'Firstly,',
  'Most importantly,',
  'For example,',
  'Furthermore,',
  'However,',
  'I strongly believe',
  'In conclusion,',
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m + ':' + String(sec).padStart(2, '0');
}

function countWords(t: string): number {
  const m = (t || '').trim().match(/\S+/g);
  return m ? m.length : 0;
}

function countSentences(t: string): number {
  const m = (t || '').trim().match(/[^.!?]+[.!?]+/g);
  return m ? m.length : 0;
}

function autoChecks(t: string): ChecksState {
  const low = (t || '').toLowerCase();
  const words = countWords(t);
  const sents = countSentences(t);
  const tailStart = Math.floor(low.length * 0.6);
  const tail = low.slice(tailStart);
  return {
    open: OPINION_CUES.some(c => low.slice(0, Math.max(120, low.length * 0.3)).includes(c)) && words >= 12,
    reasons: (low.match(/because|reason|for example|for instance|firstly|secondly/g) || []).length >= 2 && sents >= 3,
    connect: LINKERS.filter(c => low.includes(c)).length >= 2,
    ending: ENDING_CUES.some(c => tail.includes(c)) && words >= 60,
  };
}

function calcCheckedCount(text: string, checks: ChecksState): number {
  const auto = autoChecks(text);
  const keys: Array<keyof ChecksState> = ['open', 'reasons', 'connect', 'ending'];
  return keys.reduce((n, k) => {
    const on = checks[k] === true || (checks[k] !== false && auto[k]);
    return on ? n + 1 : n;
  }, 0);
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const WritingTask: React.FC<WritingTaskProps> = ({
  testId,
  initialSession,
  onComplete: _onComplete,
  onCancel,
}) => {
  const { user: authUser } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const getCurrentUser = useCallback(() => {
    if (authUser) return authUser;
    try {
      const s = localStorage.getItem('cleveroo_user');
      if (s) return JSON.parse(s);
    } catch (e) { /* ignore */ }
    return null;
  }, [authUser]);

  const currentUser = getCurrentUser();

  const LS_KEY = `cleveroo_writing_${testId}`;

  // ── Session ──
  const [session, setSession] = useState<TestSession | null>(
    (initialSession as TestSession) || null
  );
  const [loading, setLoading] = useState(!initialSession);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Results overlay ──
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState<{
    words: number; timeUsed: number; structureCount: number;
  } | null>(null);

  // ── Toast ──
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Autosave indicator ──
  const [savedBright, setSavedBright] = useState(false);
  const savePulseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Draft sync status ──
  const [isSyncing, setIsSyncing] = useState(false);
  const autosaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Test start time ──
  const testStartTimeRef = useRef<Date | null>(null);

  // ── Textarea ref for cursor ops ──
  const writeAreaRef = useRef<HTMLTextAreaElement | null>(null);

  // ─────────────────────────────────────────────
  // STATE — load from localStorage
  // ─────────────────────────────────────────────

  const getDefaultState = useCallback((timeLimit: number): WTState => ({
    text: '',
    plan: { opinion: '', r1: '', r2: '', end: '' },
    checks: {},
    seconds: timeLimit,
    done: false,
    tab: 'write',
    mode: 'practice',
  }), []);

  const loadState = useCallback((timeLimit: number): WTState => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const s: WTState = JSON.parse(raw);
        if (s && typeof s.text === 'string' && s.plan && typeof s.seconds === 'number') return s;
      }
    } catch (e) { /* ignore */ }
    return getDefaultState(timeLimit);
  }, [LS_KEY, getDefaultState]);

  const [wtState, setWTState] = useState<WTState>(() => getDefaultState(DEFAULT_TIME_LIMIT));
  const stateRef = useRef<WTState>(wtState);
  useEffect(() => { stateRef.current = wtState; }, [wtState]);

  const persistState = useCallback((s: WTState) => {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  }, [LS_KEY]);

  // ─────────────────────────────────────────────
  // FETCH SESSION
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (initialSession) {
      setSession(initialSession);
      setLoading(false);
      return;
    }
    if (!currentUser?.id) {
      setLoadError('User not authenticated');
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/start`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      })
      .then(res => {
        if (res.data.success) {
          setSession(res.data.data);
        } else {
          setLoadError(res.data.error || 'Failed to start test');
        }
      })
      .catch(err => {
        setLoadError(err.response?.data?.error || err.message || 'Failed to start test');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // ── Initialise state once session is available ──
  useEffect(() => {
    if (!session) return;
    const timeLimit = session.timeLimit || DEFAULT_TIME_LIMIT;
    const s = loadState(timeLimit);
    setWTState(s);
    testStartTimeRef.current = new Date();

    // Restore textarea value
    if (writeAreaRef.current) {
      writeAreaRef.current.value = s.text;
    }

    // Resume: try server draft (GET)
    const essayQ = session.questions.find(q =>
      q?.content?.responseFormat === 'essay' || q?.subject === 'writing'
    ) || session.questions[0];
    if (essayQ && currentUser?.id) {
      axios
        .get(
          `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/writing/draft`,
          {
            params: { sessionId: session.sessionId, questionId: essayQ.id },
            withCredentials: false,
          }
        )
        .then(res => {
          if (res.data.success && res.data.data) {
            const draft = res.data.data;
            // Only hydrate if LS is empty/older
            const lsRaw = localStorage.getItem(LS_KEY);
            if (!lsRaw) {
              const hydrated: WTState = {
                ...getDefaultState(timeLimit),
                text: draft.essayText || '',
                plan: draft.planNotes || { opinion: '', r1: '', r2: '', end: '' },
                mode: draft.mode || 'practice',
              };
              setWTState(hydrated);
              persistState(hydrated);
              if (writeAreaRef.current) {
                writeAreaRef.current.value = hydrated.text;
              }
            }
          }
        })
        .catch(() => { /* no draft yet, that's fine */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // ─────────────────────────────────────────────
  // TOAST
  // ─────────────────────────────────────────────

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2200);
  }, []);

  // ─────────────────────────────────────────────
  // AUTOSAVE to server (debounced)
  // ─────────────────────────────────────────────

  const syncToServer = useCallback((state: WTState, sessionId: string, questionId: string) => {
    if (!currentUser?.id) return;
    setIsSyncing(true);
    axios
      .post(
        `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/writing/draft`,
        {
          sessionId,
          questionId,
          essayText: state.text,
          wordCount: countWords(state.text),
          planNotes: state.plan,
          mode: state.mode,
        },
        { withCredentials: false }
      )
      .catch(() => { /* silent */ })
      .finally(() => setIsSyncing(false));
  }, [currentUser?.id, testId, API_BASE_URL]);

  const scheduleAutosave = useCallback((state: WTState) => {
    if (!session) return;
    const essayQ = session.questions.find(q =>
      q?.content?.responseFormat === 'essay' || q?.subject === 'writing'
    ) || session.questions[0];
    if (!essayQ) return;
    if (autosaveDebounceRef.current) clearTimeout(autosaveDebounceRef.current);
    autosaveDebounceRef.current = setTimeout(() => {
      syncToServer(state, session.sessionId, essayQ.id);
    }, 1500);
  }, [session, syncToServer]);

  // ─────────────────────────────────────────────
  // SAVED HINT PULSE
  // ─────────────────────────────────────────────

  const pulseSaved = useCallback(() => {
    setSavedBright(true);
    if (savePulseRef.current) clearTimeout(savePulseRef.current);
    savePulseRef.current = setTimeout(() => setSavedBright(false), 1200);
  }, []);

  // ─────────────────────────────────────────────
  // UPDATE STATE
  // ─────────────────────────────────────────────

  const updateState = useCallback((patch: Partial<WTState>) => {
    setWTState(prev => {
      const next = { ...prev, ...patch };
      persistState(next);
      return next;
    });
  }, [persistState]);

  const updateText = useCallback((text: string) => {
    setWTState(prev => {
      const next = { ...prev, text };
      persistState(next);
      scheduleAutosave(next);
      return next;
    });
    pulseSaved();
  }, [persistState, scheduleAutosave, pulseSaved]);

  const updatePlan = useCallback((key: keyof PlanState, value: string) => {
    setWTState(prev => {
      const next = { ...prev, plan: { ...prev.plan, [key]: value } };
      persistState(next);
      scheduleAutosave(next);
      return next;
    });
    pulseSaved();
  }, [persistState, scheduleAutosave, pulseSaved]);

  const toggleCheck = useCallback((key: keyof ChecksState) => {
    setWTState(prev => {
      const auto = autoChecks(prev.text);
      const currentlyOn = prev.checks[key] === true || (prev.checks[key] !== false && auto[key]);
      const next = { ...prev, checks: { ...prev.checks, [key]: !currentlyOn } };
      persistState(next);
      return next;
    });
  }, [persistState]);

  // ─────────────────────────────────────────────
  // TABS
  // ─────────────────────────────────────────────

  const setTab = useCallback((tab: 'write' | 'plan') => {
    updateState({ tab });
  }, [updateState]);

  // ─────────────────────────────────────────────
  // MODE
  // ─────────────────────────────────────────────

  const setMode = useCallback((mode: 'practice' | 'test') => {
    setWTState(prev => {
      const next: WTState = {
        ...prev,
        mode,
        tab: mode === 'test' ? 'write' : prev.tab,
      };
      persistState(next);
      return next;
    });
    showToast(
      mode === 'test'
        ? 'Test mode — hints and planning hidden'
        : 'Practice mode — hints and planning on'
    );
  }, [persistState, showToast]);

  // ─────────────────────────────────────────────
  // SENTENCE STARTERS — insert at cursor
  // ─────────────────────────────────────────────

  const insertStarter = useCallback((text: string) => {
    const ta = writeAreaRef.current;
    if (!ta) return;
    setTab('write');
    const snippet = text + ' ';
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const needsSpace = before.length > 0 && !/\s$/.test(before);
    const ins = (needsSpace ? ' ' : '') + snippet;
    const newVal = before + ins + ta.value.slice(end);
    ta.value = newVal;
    const pos = start + ins.length;
    ta.focus();
    ta.setSelectionRange(pos, pos);
    updateText(newVal);
    showToast(`Added "${text.trim()}"`);
  }, [setTab, updateText, showToast]);

  // ─────────────────────────────────────────────
  // SAVE DRAFT
  // ─────────────────────────────────────────────

  const saveDraft = useCallback(() => {
    persistState(stateRef.current);
    if (session) {
      const essayQ = session.questions.find(q =>
        q?.content?.responseFormat === 'essay' || q?.subject === 'writing'
      ) || session.questions[0];
      if (essayQ) {
        syncToServer(stateRef.current, session.sessionId, essayQ.id);
      }
    }
    showToast('Draft saved');
  }, [persistState, session, syncToServer, showToast]);

  // ─────────────────────────────────────────────
  // KEYBOARD SHORTCUTS
  // ─────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveDraft();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveDraft]);

  // ─────────────────────────────────────────────
  // TIMER
  // ─────────────────────────────────────────────

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const submitRef = useRef<((auto?: boolean) => void) | null>(null);

  useEffect(() => {
    if (!session || wtState.done || wtState.seconds <= 0) return;

    timerRef.current = setInterval(() => {
      setWTState(prev => {
        if (prev.done) return prev;
        if (prev.seconds <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          submitRef.current?.(true);
          return prev;
        }
        const next = { ...prev, seconds: prev.seconds - 1 };
        if (next.seconds % 5 === 0) persistState(next);
        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, wtState.done]);

  // ─────────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────────

  const submit = useCallback(async (auto?: boolean) => {
    if (!session || !currentUser?.id) return;
    const cur = stateRef.current;
    const words = countWords(cur.text);

    if (!auto && !cur.done) {
      if (words < 15) {
        showToast('Write a little more before submitting');
        setTab('write');
        writeAreaRef.current?.focus();
        return;
      }
      const ok = await confirm({
        title: 'Submit your writing?',
        body: 'You can still keep editing afterwards if there is time left.',
        confirmLabel: 'Submit',
        cancelLabel: 'Keep writing',
      });
      if (!ok) return;
    }

    setWTState(prev => {
      const next = { ...prev, done: true };
      persistState(next);
      return next;
    });
    if (timerRef.current) clearInterval(timerRef.current);
    setIsSubmitting(true);

    const essayQ = session.questions.find(q =>
      q?.content?.responseFormat === 'essay' || q?.subject === 'writing'
    ) || session.questions[0];
    const timeLimit = session.timeLimit || DEFAULT_TIME_LIMIT;
    const timeSpent = timeLimit - cur.seconds;

    try {
      const startTime = testStartTimeRef.current?.toISOString() ?? new Date().toISOString();
      await axios.post(
        `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/submit`,
        {
          sessionId: session.sessionId,
          startTime,
          answers: [{
            questionId: essayQ?.id,
            answer: cur.text,
            timeSpent,
            mode: cur.mode,
            planNotes: cur.plan,
          }],
        },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: false }
      );
      // Success: show results dialog (do NOT call onComplete per spec)
    } catch (err) {
      // Even on error, show local results
    } finally {
      setIsSubmitting(false);
    }

    const structureCount = calcCheckedCount(cur.text, cur.checks);
    setResultData({ words, timeUsed: timeSpent, structureCount });
    setShowResult(true);
    // Clear local storage only after showing results
    localStorage.removeItem(LS_KEY);
  }, [session, currentUser?.id, testId, API_BASE_URL, LS_KEY, persistState, showToast, setTab, confirm]);

  useEffect(() => { submitRef.current = submit; }, [submit]);

  // ─────────────────────────────────────────────
  // DERIVED
  // ─────────────────────────────────────────────

  const timeLimit = session?.timeLimit || DEFAULT_TIME_LIMIT;
  const frac = Math.max(0, wtState.seconds / timeLimit);
  const ringOffset = RING_LEN * (1 - frac);

  const timerClass = [
    'wt-timer',
    wtState.seconds <= 60 ? 'is-crit' : wtState.seconds <= 5 * 60 ? 'is-warn' : '',
  ].filter(Boolean).join(' ');

  const words = countWords(wtState.text);
  const sentences = countSentences(wtState.text);
  const chars = wtState.text.length;
  const goalPct = Math.min(100, Math.round(words / WORD_GOAL * 100));

  const planFilled = (['opinion', 'r1', 'r2', 'end'] as Array<keyof PlanState>)
    .filter(k => (wtState.plan[k] || '').trim() !== '').length;

  const auto = autoChecks(wtState.text);
  const checkedCount = calcCheckedCount(wtState.text, wtState.checks);

  const goalNote = words === 0
    ? { text: 'Aim for 200+', color: 'var(--mute)' }
    : goalPct >= 100
    ? { text: 'Goal reached!', color: 'var(--green)' }
    : goalPct >= 60
    ? { text: 'Almost there', color: 'var(--orange)' }
    : { text: 'Keep going', color: 'var(--mute)' };

  // The first essay question is the "prompt question"
  const essayQ = session
    ? session.questions.find(q =>
        q?.content?.responseFormat === 'essay' || q?.subject === 'writing'
      ) || session.questions[0]
    : null;

  // Real field priority confirmed from production shape:
  // content.promptTitle > source_metadata.naplan.promptTitle > question.title > test.title
  const promptTitle =
    essayQ?.content?.promptTitle
    || essayQ?.source_metadata?.naplan?.promptTitle
    || essayQ?.title
    || (session?.test?.title ?? '');

  // content.genre > source_metadata.naplan.genre, capitalized for display
  const genreRaw =
    essayQ?.content?.genre
    || essayQ?.source_metadata?.naplan?.genre
    || 'Writing';
  const genre = genreRaw.charAt(0).toUpperCase() + genreRaw.slice(1);

  // content.fullPromptText > content.questionText > content.question (all hold prompt body)
  const promptLead =
    (essayQ?.content?.fullPromptText as string | undefined)
    || (essayQ?.content?.questionText as string | undefined)
    || (essayQ?.content?.question as string | undefined)
    || '';

  // Optional planning hint shown in Plan tab (may be empty)
  const planningInstructions = (essayQ?.content?.planningInstructions as string | undefined) || '';

  const CHECK_KEYS: Array<keyof ChecksState> = ['open', 'reasons', 'connect', 'ending'];
  const CHECK_LABELS: Record<string, { label: string; hint: string }> = {
    open: { label: 'Opening opinion', hint: 'You told the reader what you think' },
    reasons: { label: 'Two or more reasons', hint: 'Each backed by an example' },
    connect: { label: 'Linking words', hint: 'Firstly, because, however…' },
    ending: { label: 'Strong conclusion', hint: 'Restate your opinion at the end' },
  };

  // ─────────────────────────────────────────────
  // RESULT DIALOG TEXT
  // ─────────────────────────────────────────────

  const getResultText = (rd: { words: number; structureCount: number }) => {
    const firstName = (currentUser?.name || '').split(' ')[0] || '';
    const name = firstName ? `, ${firstName}` : '';
    if (rd.structureCount >= 4 && rd.words >= WORD_GOAL)
      return { title: `Brilliant work${name}!`, sub: 'Clear opinion, strong reasons and a tidy ending. Nicely done.' };
    if (rd.structureCount >= 2)
      return { title: `Writing submitted${name}!`, sub: "Good effort — your teacher will read it and send feedback soon." };
    return { title: `Good start${name}!`, sub: "It's in. Next time, try adding another reason and a strong ending." };
  };

  // ─────────────────────────────────────────────
  // LOADING / ERROR
  // ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="wt-root">
        <div className="wt-loading">
          <div className="wt-loading__spinner" />
          <span className="wt-loading__text">Loading writing task…</span>
        </div>
      </div>
    );
  }

  if (loadError || !session) {
    return (
      <div className="wt-root">
        <div className="wt-error">
          <h2>Couldn't load the test</h2>
          <p>{loadError || 'Unknown error'}</p>
          <button className="wt-btn wt-btn--ghost" onClick={onCancel}>Back to dashboard</button>
        </div>
      </div>
    );
  }

  const barSubtitle = wtState.mode === 'test'
    ? 'Test conditions · no hints or planning · auto-saved'
    : 'Plan, then write your response · auto-saved as you go';

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  return (
    <div className={`wt-root${wtState.mode === 'test' ? ' is-test' : ''}`}>

      {/* ── APP BAR ── */}
      <header className="wt-bar">
        <div className="wt-bar__inner">
          <span className="wt-bar__brand">
            <img src={logoUrl} alt="Cleveroo" />
            <b>cleve<span className="wt-r">r</span><span className="wt-o">oo</span></b>
          </span>
          <div className="wt-bar__divider" />
          <div className="wt-bar__test">
            <h1>{session.test.title}</h1>
            <small>{barSubtitle}</small>
          </div>
          <div className="wt-bar__right">

            {/* Mode switch */}
            <div className="wt-modesw" role="group" aria-label="Mode">
              <button
                data-mode="practice"
                className={wtState.mode === 'practice' ? 'is-active' : ''}
                onClick={() => wtState.mode !== 'practice' && setMode('practice')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2h6c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z"/>
                  <path d="M9 18h6M10 22h4"/>
                </svg>
                <span>Practice</span>
              </button>
              <button
                data-mode="test"
                className={wtState.mode === 'test' ? 'is-active' : ''}
                onClick={() => wtState.mode !== 'test' && setMode('test')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="11" width="14" height="10" rx="2"/>
                  <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                </svg>
                <span>Test</span>
              </button>
            </div>

            {/* Timer */}
            <div className={timerClass}>
              <svg className="wt-timer__ring" viewBox="0 0 26 26">
                <circle className="wt-trk" cx="13" cy="13" r="11" />
                <circle
                  className="wt-val"
                  cx="13" cy="13" r="11"
                  style={{ strokeDasharray: RING_LEN, strokeDashoffset: ringOffset }}
                />
              </svg>
              <span className="wt-timer__txt">
                <span className="wt-timer__num">{fmtTime(wtState.seconds)}</span>
                <span className="wt-timer__lbl">remaining</span>
              </span>
            </div>

            <SoundMuteToggle />

            {/* Exit */}
            <button
              className="wt-bar__exit"
              onClick={async () => {
                if (await confirm({
                  title: 'Exit the activity?',
                  body: 'Your writing is saved — you can come back and finish later.',
                  confirmLabel: 'Exit',
                  cancelLabel: 'Keep writing',
                })) {
                  onCancel();
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <path d="m16 17 5-5-5-5"/>
                <path d="M21 12H9"/>
              </svg>
              Exit
            </button>
          </div>
        </div>
      </header>

      {/* ── MAIN SHELL ── */}
      <main className="wt-shell">
        <div className="wt-col">

          {/* ── PROMPT CARD ── */}
          <section className="wt-prompt">
            <div className="wt-prompt__top">
              <span className="wt-tag wt-tag--type">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m18 2 4 4-14 14H4v-4z"/>
                  <path d="m14.5 5.5 4 4"/>
                </svg>
                Writing task
              </span>
              <span className="wt-tag wt-tag--genre">{genre}</span>
              <span className="wt-prompt__meta">
                Suggested: plan <b>5 min</b> &middot; write <b>30 min</b>
              </span>
            </div>

            <h2 className="wt-prompt__title">{promptTitle}</h2>

            <div className="wt-prompt__body">
              {/* Stimulus image placeholder */}
              <div className="wt-prompt__stim">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="9" r="6"/>
                  <path d="M12 15v6M9 19h6"/>
                </svg>
                <span>STIMULUS IMAGE</span>
              </div>
              <div>
                {promptLead ? (
                  <p className="wt-prompt__lead">{promptLead}</p>
                ) : (
                  <p className="wt-prompt__lead">
                    Write a response to the task above. Plan your ideas first, then write your best work.
                  </p>
                )}
                {/* Guidance chips — hidden in test mode via CSS */}
                <div className="wt-prompt__checks">
                  <span className="wt-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    State your opinion clearly
                  </span>
                  <span className="wt-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    Give 2&ndash;3 strong reasons
                  </span>
                  <span className="wt-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    Check spelling &amp; punctuation
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ── COMPOSER ── */}
          <section className="wt-composer">
            <div className="wt-composer__tabs">
              {/* Write tab */}
              <button
                className={`wt-tab${wtState.tab === 'write' ? ' is-active' : ''}`}
                data-pane="write"
                onClick={() => setTab('write')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/>
                </svg>
                Write
              </button>
              {/* Plan tab — hidden in test mode via CSS */}
              <button
                className={`wt-tab${wtState.tab === 'plan' ? ' is-active' : ''}`}
                data-pane="plan"
                onClick={() => setTab('plan')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h5"/>
                  <path d="M9 3H4a1 1 0 0 0-1 1v3"/>
                  <path d="M14 3h6a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/>
                </svg>
                Plan
                <span className="wt-num">{planFilled}</span>
              </button>
              {/* Saved indicator */}
              <span className={`wt-composer__save${savedBright ? ' is-bright' : ''}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
                {isSyncing ? 'Saving…' : 'Saved'}
              </span>
            </div>

            {/* WRITE PANE */}
            <div className={`wt-pane wt-write${wtState.tab === 'write' ? ' is-active' : ''}`}>
              <textarea
                ref={writeAreaRef}
                className="wt-write__area"
                defaultValue={wtState.text}
                placeholder="Start your response here. Open with your opinion, then give your strongest reason first…"
                spellCheck
                aria-label="Your writing"
                onFocus={playSelect}
                onInput={e => updateText((e.target as HTMLTextAreaElement).value)}
              />
              <div className="wt-write__foot">
                <span><b>{words}</b> words</span>
                <span className="wt-write__sep" />
                <span><b>{sentences}</b> sentences</span>
                <span className="wt-write__sep" />
                <span><b>{chars}</b> characters</span>
              </div>
            </div>

            {/* PLAN PANE */}
            <div className={`wt-pane wt-plan${wtState.tab === 'plan' ? ' is-active' : ''}`}>
              <p className="wt-plan__lead">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2h6c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z"/>
                  <path d="M9 18h6M10 22h4"/>
                </svg>
                {planningInstructions
                  ? planningInstructions
                  : 'Jot your ideas first — this is just for you and won’t be marked.'}
              </p>
              <div className="wt-planbox wt-planbox--opinion">
                <label className="wt-planbox__lbl"><span className="wt-dot">!</span>My opinion</label>
                <textarea
                  placeholder="I think every student should / should not…"
                  value={wtState.plan.opinion}
                  onChange={e => updatePlan('opinion', e.target.value)}
                />
              </div>
              <div className="wt-planbox wt-planbox--r1">
                <label className="wt-planbox__lbl"><span className="wt-dot">1</span>Reason one + example</label>
                <textarea
                  placeholder="My first reason is… For example…"
                  value={wtState.plan.r1}
                  onChange={e => updatePlan('r1', e.target.value)}
                />
              </div>
              <div className="wt-planbox wt-planbox--r2">
                <label className="wt-planbox__lbl"><span className="wt-dot">2</span>Reason two + example</label>
                <textarea
                  placeholder="Another reason is… For instance…"
                  value={wtState.plan.r2}
                  onChange={e => updatePlan('r2', e.target.value)}
                />
              </div>
              <div className="wt-planbox wt-planbox--end">
                <label className="wt-planbox__lbl"><span className="wt-dot">3</span>Strong ending</label>
                <textarea
                  placeholder="To sum up, … so I believe…"
                  value={wtState.plan.end}
                  onChange={e => updatePlan('end', e.target.value)}
                />
              </div>
            </div>
          </section>

        </div>

        {/* ── RIGHT RAIL (hidden in test mode via CSS) ── */}
        <aside className="wt-rail">

          {/* Word goal panel */}
          <div className="wt-panel">
            <div className="wt-panel__head">
              <span className="wt-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 18l5-6 4 4 8-10"/>
                </svg>
              </span>
              Word goal
              <span className="wt-pct">{goalPct}%</span>
            </div>
            <div className="wt-pbar">
              <div className="wt-pbar__fill" style={{ width: `${goalPct}%` }} />
            </div>
            <div className="wt-pmeta">
              <span className="wt-k"><b>{words}</b> of 200 words</span>
              <span style={{ color: goalNote.color, fontWeight: 600, fontSize: 12.5 }}>{goalNote.text}</span>
            </div>
          </div>

          {/* Structure check panel */}
          <div className="wt-panel">
            <div className="wt-panel__head">
              <span className="wt-ico alt">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </span>
              Structure check
              <span className="wt-pct" style={{ fontSize: 14, color: 'var(--mute)' }}>{checkedCount}/4</span>
            </div>
            <div className="wt-check">
              {CHECK_KEYS.map(key => {
                const on = wtState.checks[key] === true || (wtState.checks[key] !== false && auto[key]);
                const label = CHECK_LABELS[key];
                return (
                  <button
                    key={key}
                    className={`wt-check__item${on ? ' is-done' : ''}`}
                    onClick={() => toggleCheck(key)}
                  >
                    <span className="wt-check__box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5"/>
                      </svg>
                    </span>
                    <span className="wt-check__txt">
                      {label.label}
                      <small>{label.hint}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sentence starters panel */}
          <div className="wt-panel">
            <div className="wt-panel__head">
              <span className="wt-ico alt2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 3 1.9 5.8H20l-4.9 3.6 1.9 5.8L12 14.6 7 18.2l1.9-5.8L4 8.8h6.1z"/>
                </svg>
              </span>
              Sentence starters
            </div>
            <div className="wt-starters">
              {SENTENCE_STARTERS.map(s => (
                <button key={s} className="wt-starter" onClick={() => insertStarter(s)}>
                  {s}
                </button>
              ))}
            </div>
            <p className="wt-starters__note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14m-6-6 6 6-6 6"/>
              </svg>
              Tap one to drop it into your writing.
            </p>
          </div>

        </aside>
      </main>

      {/* ── STICKY NAV ── */}
      <nav className="wt-navbar">
        <div className="wt-navbar__inner">
          <button className="wt-btn wt-btn--ghost" onClick={saveDraft}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <path d="M17 21v-8H7v8M7 3v5h8"/>
            </svg>
            <span className="lbl">Save draft</span>
          </button>
          <div className="wt-navbar__status">
            <span><span className="wt-wc">{words}</span> words written</span>
            <span className="wt-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
              Auto-saved
            </span>
          </div>
          <button
            className="wt-btn wt-btn--submit"
            disabled={isSubmitting}
            onClick={() => submit(false)}
          >
            <span className="lbl">{isSubmitting ? 'Submitting…' : 'Submit writing'}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14m-6-6 6 6-6 6"/>
            </svg>
          </button>
        </div>
      </nav>

      {/* ── RESULTS OVERLAY ── */}
      {showResult && resultData && (() => {
        const { title, sub } = getResultText(resultData);
        const used = resultData.timeUsed;
        return (
          <div className="wt-scrim is-open">
            <div className="wt-dialog">
              <div className="wt-dialog__badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2 11 13"/>
                  <path d="m22 2-7 20-4-9-9-4z"/>
                </svg>
              </div>
              <h2>{title}</h2>
              <p>Your teacher will read it and send feedback soon. Here&apos;s a snapshot.</p>
              <p style={{ marginTop: 4, fontSize: 13, color: 'var(--mute)' }}>{sub}</p>
              <div className="wt-dialog__score">
                <div className="wt-scorebox">
                  <b>{resultData.words}</b>
                  <small>Words</small>
                </div>
                <div className="wt-scorebox alt">
                  <b>{fmtTime(used)}</b>
                  <small>Time used</small>
                </div>
                <div className="wt-scorebox alt2">
                  <b>{resultData.structureCount}/4</b>
                  <small>Structure</small>
                </div>
              </div>
              <div className="wt-dialog__actions">
                <button
                  className="wt-btn wt-btn--ghost"
                  onClick={() => {
                    setShowResult(false);
                    setTab('write');
                    writeAreaRef.current?.focus();
                  }}
                >
                  Keep editing
                </button>
                <button className="wt-btn wt-btn--primary" onClick={onCancel}>
                  Back to dashboard
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── TOAST ── */}
      <div className={`wt-toast${toastVisible ? ' is-show' : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5"/>
        </svg>
        <span>{toastMsg}</span>
      </div>

      {confirmDialog}
    </div>
  );
};

export default WritingTask;
